import type { RequestHeaders, RequestHeaderValue } from './types';

/**
 * Applies one entry. The value shape decides what happens, and the three
 * lookalikes are the point: `null` removes, `undefined` contributes nothing,
 * and `''`, `0` and `false` are ordinary values.
 */
function apply(
  headers: Headers,
  name: string,
  value: RequestHeaderValue,
): void {
  if (value === undefined) return;
  if (value === null) {
    headers.delete(name);
    return;
  }
  if (typeof value === 'object') {
    // An array sets the header once per entry, so an empty one sets nothing.
    //
    // The `delete` first, here and in the `null` branch above, is what keeps an
    // array from EXTENDING a value instead of replacing it. It bites only when
    // the record `toHeaders` walks carries this header name twice under two
    // spellings -- `{ accept: 'a', Accept: [] }` are two record keys and one
    // header -- because `Headers` starts empty and each record key arrives once.
    headers.delete(name);
    for (const item of value) headers.append(name, String(item));
    return;
  }
  headers.set(name, String(value));
}

/** Writes `source` into `target`. Mutates, so `target` is never a caller's own. */
function overlay(target: RequestHeaders, source: RequestHeaders): void {
  for (const [name, value] of Object.entries(source)) {
    // Skipped rather than copied: `undefined` contributes nothing, so copying it
    // would erase the value it is laid over instead of leaving it alone.
    if (value === undefined) continue;
    // `defineProperty` and not `target[name] = value`: a source record may carry
    // an own `__proto__` key, and plain assignment hits the prototype SETTER
    // instead of making an own key. That made the SAME caller record produce two
    // different wires -- the key survived when there was no global to merge with,
    // and vanished when there was (RH13).
    //
    // `__proto__` is the only name that does this: it is the one own-property
    // lookalike with a setter on `Object.prototype`. MEASURED that `constructor`,
    // `toString` and `valueOf` are ordinary data properties there, so plain
    // assignment already made an own key for those and they never leaked.
    Object.defineProperty(target, name, {
      configurable: true,
      enumerable: true,
      value,
      writable: true,
    });
  }
}

/**
 * The global headers with the per-call ones on top, so the call wins on a
 * collision (RH2).
 *
 * This is a record, not a `Headers`, and it is settled BEFORE the first `before`
 * handler runs — that is the whole point (HE22, HE31). A handler that only adds
 * an `authorization` when there is none yet has to be able to see the global it
 * would collide with, and it reads that off a plain record it may spread.
 *
 * A removal stays a `null` here rather than being resolved away, because the
 * handlers may still turn it back into a value. `toHeaders` is where the value
 * shapes are finally spent.
 */
export function mergeHeaders(
  global: RequestHeaders,
  perCall: RequestHeaders | undefined,
): RequestHeaders {
  // A merge with nothing to merge copies nothing, and the two sides of that are
  // both required rather than an optimisation. A global that contributes a name
  // FORCES a new record, because RQ20 forbids writing it into the caller's own.
  // With no global there is nothing to write, and BE31 needs exactly that record
  // to reach the handler, so that a handler mutating it in place still touches
  // what the caller passed.
  if (Object.keys(global).length === 0) return perCall ?? {};

  const mergedHeaders: RequestHeaders = {};
  overlay(mergedHeaders, global);
  overlay(mergedHeaders, perCall ?? {});
  return mergedHeaders;
}

/**
 * Turns the record the handlers left behind into the object the transport gets.
 *
 * Throws the platform `TypeError` on an invalid name or value; the caller wraps
 * it. Folding is left to `Headers`: measured, it joins `cookie` with `'; '` and
 * everything else with `', '`.
 */
export function toHeaders(headers: RequestHeaders): Headers {
  const wireHeaders = new Headers();
  for (const [name, value] of Object.entries(headers))
    apply(wireHeaders, name, value);
  return wireHeaders;
}

/**
 * The headers a call goes out with, once the caller's own `requestId` is folded
 * in.
 *
 * Separate from `toHeaders` because it answers a different question: that one
 * turns whatever shape the caller held into a `Headers`, this one settles which
 * of two ways of naming the same header wins. The named option does, because of
 * the three ways an `x-request-id` can appear -- this option, a raw header, and
 * something `trace` mints -- it is the one the caller reached for deliberately
 * (TR6, TR20).
 *
 * Throws when the value cannot be a header value, and the call site turns that
 * into an `UnsupportedRequestError`. Unlike a value inherited through
 * `traceFrom`, which is dropped (TR17), this one is the caller's own and saying
 * so is the only way they find out.
 */
export function withRequestId(
  headers: Headers,
  requestId: string | undefined,
): Headers {
  if (requestId === undefined) return headers;
  // An empty id names nothing. Left alone it put an empty `x-request-id` on the
  // wire, so a caller who thought they were naming their request named nothing
  // and heard nothing about it (TR20).
  if (requestId === '')
    throw new TypeError('`requestId` is empty, which names no request');
  headers.set('x-request-id', requestId);
  return headers;
}
